import React, { useState, useEffect } from 'react';
import Table from '../common/Table';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Card from '../common/Card';
import api from '../../services/api';
import { validateEmail, validatePhone } from '../../utils/validators';
import '../../styles/supplier-list.css';

const SupplierList = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    supplier_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    payment_terms: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/suppliers.php');
      setSuppliers(response.data.suppliers);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplier_name: supplier.supplier_name,
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      payment_terms: supplier.payment_terms || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    
    try {
      await api.delete(`/suppliers.php/${id}`);
      loadSuppliers();
    } catch (error) {
      console.error('Failed to delete supplier:', error);
      alert(error.response?.data?.error || 'Failed to delete supplier');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplier_name.trim()) {
      newErrors.supplier_name = 'Supplier name is required';
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (editingSupplier) {
        await api.put(`/suppliers.php/${editingSupplier.id}`, formData);
      } else {
        await api.post('/suppliers.php', formData);
      }
      
      setShowModal(false);
      setFormData({
        supplier_name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        payment_terms: ''
      });
      setEditingSupplier(null);
      loadSuppliers();
    } catch (error) {
      console.error('Failed to save supplier:', error);
      alert(error.response?.data?.error || 'Failed to save supplier');
    }
  };

  const columns = [
    {
      key: 'supplier_name',
      label: 'Supplier'
    },
    {
      key: 'contact_person',
      label: 'Contact Person',
      render: (value) => value || '-'
    },
    {
      key: 'phone',
      label: 'Phone'
    },
    {
      key: 'email',
      label: 'Email',
      render: (value) => value || '-'
    },
    {
      key: 'payment_terms',
      label: 'Payment Terms',
      render: (value) => value || '-'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button 
            className="icon-btn edit"
            onClick={() => handleEdit(row)}
          >
            ✏️
          </button>
          <button 
            className="icon-btn delete"
            onClick={() => handleDelete(row.id)}
          >
            🗑️
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="supplier-list">
      <Card>
        <div className="supplier-list-header">
          <h2>Suppliers</h2>
          <Button 
            variant="primary"
            onClick={() => {
              setEditingSupplier(null);
              setFormData({
                supplier_name: '',
                contact_person: '',
                email: '',
                phone: '',
                address: '',
                payment_terms: ''
              });
              setShowModal(true);
            }}
            icon="➕"
          >
            Add Supplier
          </Button>
        </div>

        <Table
          columns={columns}
          data={suppliers}
          loading={loading}
        />

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
        >
          <form onSubmit={handleSubmit} className="supplier-form">
            <Input
              label="Supplier Name"
              value={formData.supplier_name}
              onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
              error={errors.supplier_name}
              required
              autoFocus
            />
            
            <Input
              label="Contact Person"
              value={formData.contact_person}
              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
            />

            <Input
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              error={errors.phone}
              placeholder="0712345678"
            />

            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={errors.email}
            />

            <Input
              label="Address"
              type="textarea"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows="3"
            />

            <Input
              label="Payment Terms"
              value={formData.payment_terms}
              onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
              placeholder="e.g., Net 30"
            />

            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                {editingSupplier ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>
      </Card>
    </div>
  );
};

export default SupplierList;